import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getItem } from "../../../../../utils/localStorage";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { update_lease_template } from "../../../../../utils/urls";

// Extend Quill Image format to preserve styles
const Image = Quill.import('formats/image');
class CustomImage extends Image {
  static formats(domNode) {
    return {
      alt: domNode.getAttribute('alt'),
      height: domNode.style.height || domNode.getAttribute('height'),
      width: domNode.style.width || domNode.getAttribute('width'),
      style: domNode.getAttribute('style')
    };
  }

  format(name, value) {
    if (name === 'height' || name === 'width' || name === 'style') {
      if (value) {
        this.domNode.setAttribute(name, value);
        if (name === 'width') this.domNode.style.width = value;
        if (name === 'height') this.domNode.style.height = value;
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(CustomImage, true);

const EditTemplateDialog = ({ template, visible, onHide, onEdit }) => {
  const [templateName, setTemplateName] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [userId, setUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageResizeVisible, setImageResizeVisible] = useState(false);
  const [currentImageSize, setCurrentImageSize] = useState({ width: 100, height: 'auto' });
  const [loading, setLoading] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    if (template) {
      setTemplateName(template.name || "");
      setTemplateContent(template.templateContent || "");
      setTemplateDescription(template.description || "");
    }
  }, [template]);

  useEffect(() => {
    if (visible) {
      getCurrentUser();
    }
  }, [visible]);

  // Handle image click for resizing
  useEffect(() => {
    const handleImageClick = (e) => {
      if (e.target.tagName === 'IMG' && e.target.closest('.ql-editor')) {
        e.preventDefault();
        setSelectedImage(e.target);

        // Get current image dimensions
        const computedStyle = window.getComputedStyle(e.target);
        const width = e.target.style.width || computedStyle.width;
        const height = e.target.style.height || computedStyle.height;

        setCurrentImageSize({
          width: parseInt(width) || 100,
          height: height === 'auto' ? 'auto' : (parseInt(height) || 'auto')
        });

        setImageResizeVisible(true);
      }
    };

    if (visible) {
      document.addEventListener('click', handleImageClick);
      return () => document.removeEventListener('click', handleImageClick);
    }
  }, [visible]);

  const getCurrentUser = async () => {
    try {
      const currentUserValue = await getItem("APPUSER");
      let currentUser = typeof currentUserValue === "string" ? JSON.parse(currentUserValue) : currentUserValue;
      if (currentUser?.user?.userId) {
        setUserId(currentUser.user.userId);
      }
    } catch (err) {
      console.error("Error getting user:", err.message);
    }
  };

  const insertTextAtCursor = (text) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
      editor.insertText(range.index, text);
      editor.setSelection(range.index + text.length);
      editor.focus();
    }
  };

  // Image resize functions
  const handleImageResize = () => {
    if (selectedImage) {
      const width = `${currentImageSize.width}%`;
      const height = currentImageSize.height === 'auto' ? 'auto' : `${currentImageSize.height}px`;

      selectedImage.style.width = width;
      selectedImage.style.height = height;
      selectedImage.setAttribute('width', width);
      selectedImage.setAttribute('height', height);

      const styleAttr = `width: ${width}; height: ${height}; max-width: 100%;`;
      selectedImage.setAttribute('style', styleAttr);

      // Force update by getting the HTML directly
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const html = editor.root.innerHTML;
        setTemplateContent(html);
      }

      setImageResizeVisible(false);
      setSelectedImage(null);
    }
  };

  const applyImageSize = (sizeType) => {
    if (selectedImage) {
      let width = '100%';
      switch (sizeType) {
        case 'small':
          width = '25%';
          break;
        case 'medium':
          width = '50%';
          break;
        case 'large':
          width = '75%';
          break;
        case 'full':
          width = '100%';
          break;
        default:
          width = '100%';
      }

      const height = 'auto';
      selectedImage.style.width = width;
      selectedImage.style.height = height;
      selectedImage.setAttribute('width', width);
      selectedImage.setAttribute('height', height);

      const styleAttr = `width: ${width}; height: ${height}; max-width: 100%;`;
      selectedImage.setAttribute('style', styleAttr);

      // Force update by getting the HTML directly
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const html = editor.root.innerHTML;
        setTemplateContent(html);
      }

      setImageResizeVisible(false);
      setSelectedImage(null);
    }
  };

  const removeImage = () => {
    if (selectedImage) {
      selectedImage.remove();

      // Force update by getting the HTML directly
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const html = editor.root.innerHTML;
        setTemplateContent(html);
      }

      setImageResizeVisible(false);
      setSelectedImage(null);
    }
  };

  // Image upload handler
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toastify('Image file size must be less than 5MB', 'error');
          return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
          toastify('Please select a valid image file', 'error');
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const imageUrl = e.target?.result;
            const editor = quillRef.current?.getEditor();
            if (editor && imageUrl) {
              const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
              editor.insertEmbed(range.index, 'image', imageUrl);
              editor.setSelection(range.index + 1);
            }
          } catch (error) {
            console.error('Error inserting image:', error);
            toastify('Error inserting image', 'error');
          }
        };
        reader.onerror = () => {
          toastify('Error reading image file', 'error');
        };
        reader.readAsDataURL(file);
      }
    };
  }, []);

  // Enhanced Quill modules configuration
  const modules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['blockquote', 'code-block'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), [imageHandler]);

  // Enhanced formats for better rich text support
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'link', 'image', 'video',
    'blockquote', 'code-block'
  ];

  // Define all placeholders directly in component
  const placeholders = {
    basicInformation: [
      { label: "Unit Name", placeholder: "{unit.name}", description: "Name of the unit" },
      { label: "Unit Floor Number", placeholder: "{unit.floorUnitNo}", description: "Floor and unit number" },
      { label: "Unit Type", placeholder: "{unit.unitType}", description: "Type of unit (apartment, house, etc.)" },
      { label: "Division", placeholder: "{unit.division}", description: "Division of the property" },
      { label: "Land Rate Number", placeholder: "{unit.landRateNumber}", description: "Land rate number of the unit" },
      { label: "Property Address", placeholder: "{property.address}", description: "Full address of the property" },
      { label: "Land Reference Number", placeholder: "{property.landReferenceNumber}", description: "Land reference number" },
      { label: "Lease Status", placeholder: "{lease.status}", description: "Status of the lease agreement" }
    ],
    tenantDetails: [
      { label: "Tenant First Name", placeholder: "{tenant.firstName}", description: "First name of the tenant" },
      { label: "Tenant Last Name", placeholder: "{tenant.lastName}", description: "Last name of the tenant" },
      { label: "Tenant Phone", placeholder: "{tenant.phoneNumber}", description: "Tenant's phone number" },
      { label: "Tenant Email", placeholder: "{tenant.email}", description: "Tenant's email address" },
      { label: "Tenant ID Number", placeholder: "{tenant.idNumber}", description: "Tenant's identification number" },
      { label: "Tenant Next of Kin Name", placeholder: "{tenant.nextOfKinName}", description: "Name of tenant's next of kin" },
      { label: "Tenant Next of Kin Relationship", placeholder: "{tenant.nextOfKinRelationship}", description: "Relationship with next of kin" },
      { label: "Tenant Next of Kin Contact", placeholder: "{tenant.nextOfKinContact}", description: "Contact of next of kin" },
    ],
    landlordDetails: [
      { label: "Landlord First Name", placeholder: "{landlord.firstName}", description: "First name of the landlord" },
      { label: "Landlord Last Name", placeholder: "{landlord.lastName}", description: "Last name of the landlord" },
      { label: "Landlord Phone", placeholder: "{landlord.phoneNumber}", description: "Landlord's phone number" },
      { label: "Landlord Email", placeholder: "{landlord.email}", description: "Landlord's email address" },
      { label: "Landlord ID Number", placeholder: "{landlord.idNumber}", description: "Landlord's identification number" }
    ],
    leaseTerms: [
      { label: "Start Date", placeholder: "{lease.leaseTerms.startDate}", description: "When the lease begins" },
      { label: "End Date", placeholder: "{lease.leaseTerms.endDate}", description: "When the lease ends" },
      { label: "Lease Duration (Months)", placeholder: "{lease.leaseTerms.duration}", description: "Total duration of the lease in months" },
      { label: "Auto Renewal", placeholder: "{lease.leaseTerms.autoRenewal ? 'Yes' : 'No'}", description: "Whether the lease auto-renews" }
    ],
    financialTerms: [
      { label: "Currency Name", placeholder: "{currency.currencyName}", description: "Name of the currency" },
      { label: "Currency Code", placeholder: "{currency.currencyShortCode}", description: "Currency short code" },
      { label: "Monthly Rent", placeholder: "{lease.financialTerms.monthlyRent}", description: "Monthly rental amount" },
      { label: "Security Deposit", placeholder: "{lease.financialTerms.securityDeposit}", description: "Required security deposit" },
      { label: "Deposit Months", placeholder: "{lease.financialTerms.depositMonths}", description: "Number of months for security deposit" },
      { label: "Balance Brought Forward", placeholder: "{lease.financialTerms.balanceBroughtForward}", description: "Balance brought forward from previous agreement" },
      { label: "Payment Due Date", placeholder: "{lease.financialTerms.paymentDueDate}", description: "When rent is due" },
      { label: "Currency + Rent", placeholder: "{currency.currencyShortCode} {lease.financialTerms.monthlyRent}", description: "Currency code and monthly rent" },
      { label: "Currency + Deposit", placeholder: "{currency.currencyShortCode} {lease.financialTerms.securityDeposit}", description: "Currency code and security deposit" }
    ],
    billingCycle: [
      { label: "Billing Frequency", placeholder: "{lease.billingCycle.frequency}", description: "How often billing occurs" },
      { label: "Next Invoice Date", placeholder: "{lease.billingCycle.nextInvoiceDate}", description: "Date of the next invoice" },
      { label: "Auto Send Invoices", placeholder: "{lease.billingCycle.autoSend ? 'Yes' : 'No'}", description: "Whether invoices are sent automatically" }
    ],
    paymentMethods: [
      { label: "Payment Method Type", placeholder: "{lease.financialTerms.paymentMethods[0].type}", description: "Type of payment method" },
      { label: "Is Primary Payment Method", placeholder: "{lease.financialTerms.paymentMethods[0].isPrimary ? 'Yes' : 'No'}", description: "Whether this is the primary payment method" },
      { label: "Bank Name", placeholder: "{lease.financialTerms.paymentMethods[0].details.bankName}", description: "Bank name for transfer" },
      { label: "Account Name", placeholder: "{lease.financialTerms.paymentMethods[0].details.accountName}", description: "Account holder name" },
      { label: "Account Number", placeholder: "{lease.financialTerms.paymentMethods[0].details.accountNumber}", description: "Account number for payment" },
      { label: "Branch", placeholder: "{lease.financialTerms.paymentMethods[0].details.branch}", description: "Bank branch" },
      { label: "Swift Code", placeholder: "{lease.financialTerms.paymentMethods[0].details.swiftCode}", description: "Swift code for international transfers" },
      { label: "All Payment Methods", placeholder: "{lease.financialTerms.paymentMethods[*].type}", description: "Displays all payment methods" }
    ],
    mobilePayment: [
      { label: "Business Number", placeholder: "{lease.financialTerms.mpesaDetails.businessNumber}", description: "M-Pesa business number" },
      { label: "Account Number", placeholder: "{lease.financialTerms.mpesaDetails.accountNumber}", description: "M-Pesa account number" },
      { label: "Phone Number", placeholder: "{lease.financialTerms.mpesaDetails.phoneNumber}", description: "M-Pesa phone number" }
    ],
    penaltiesAndReminders: [
      { label: "Penalty Name", placeholder: "{penalty.name}", description: "Name of the penalty" },
      { label: "Penalty Type", placeholder: "{penalty.type}", description: "Type of penalty (fixed or percentage)" },
      { label: "Penalty Amount", placeholder: "{penalty.amount}", description: "Fixed amount of the penalty" },
      { label: "Penalty Percentage", placeholder: "{penalty.percentage}", description: "Percentage value of the penalty" },
      { label: "Reminder Name", placeholder: "{reminder.name}", description: "Name of the reminder" },
      { label: "Notification Types", placeholder: "{reminder.notificationTypes}", description: "Types of notifications for reminders" }
    ],
  };

  // Filter placeholders based on search term
  const filterPlaceholders = (fields) => {
    if (!searchTerm) return fields;

    return fields.filter(field =>
      field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.placeholder.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Quick format insertion functions
  const insertQuickFormat = (formatType) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      console.warn('Editor not available');
      return;
    }

    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };

    switch (formatType) {
      case 'signature':
        editor.insertText(range.index, '\n\nSignature: ______________________     Date: __________\n\n');
        break;
      case 'table':
        try {
          const tableHtml = `
<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">
  <tr>
    <th style="border: 1px solid #ddd; padding: 8px; background-color: #f5f5f5;">Header 1</th>
    <th style="border: 1px solid #ddd; padding: 8px; background-color: #f5f5f5;">Header 2</th>
  </tr>
  <tr>
    <td style="border: 1px solid #ddd; padding: 8px;">Cell 1</td>
    <td style="border: 1px solid #ddd; padding: 8px;">Cell 2</td>
  </tr>
</table>`;
          editor.clipboard.dangerouslyPasteHTML(range.index, tableHtml);
        } catch (error) {
          // Fallback to simple table text if HTML insertion fails
          const tableText = `
Header 1          | Header 2
------------------|------------------
Cell 1            | Cell 2
`;
          editor.insertText(range.index, tableText);
        }
        break;
      case 'date':
        const today = new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        editor.insertText(range.index, today);
        break;
      case 'separator':
        editor.insertText(range.index, '\n\n───────────────────────────────────────\n\n');
        break;
      default:
        break;
    }
  };

  // Render the placeholders by category (simple button style like edit template)
  const renderPlaceholderSection = (category, fields) => {
    const filteredFields = filterPlaceholders(fields);

    if (filteredFields.length === 0) return null;

    return (
      <div key={category} className="field-category mb-4">
        <h6 className="text-uppercase mb-3 fw-bold" style={{ color: '#2196f3' }}>
          {category.replace(/([A-Z])/g, ' $1').trim()}
        </h6>
        <div className="d-flex flex-wrap gap-2">
          {filteredFields.map((field, index) => (
            <button
              key={index}
              className="btn btn-outline-secondary btn-sm"
              onClick={() => insertTextAtCursor(field.placeholder)}
              title={`${field.description}: ${field.placeholder}`}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
            >
              {field.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toastify("Template name is required", "error");
      return;
    }

    if (!templateDescription.trim()) {
      toastify("Template description is required", "error");
      return;
    }

    // Get fresh content from editor to ensure all inline styles are captured
    const editor = quillRef.current?.getEditor();
    const currentContent = editor ? editor.root.innerHTML : templateContent;

    if (!currentContent.trim()) {
      toastify("Template content is required", "error");
      return;
    }

    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      const data = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        templateContent: currentContent.trim()
      };

      if (userId) {
        data.createdBy = userId;
      }

      const response = await makeRequest2(
        `${update_lease_template}/${facilityId}/${template._id}`,
        "POST",
        data
      );

      if (response.success) {
        toastify("Template updated successfully", "success");
        onEdit(); // Refresh the parent component
        onHide();
        resetForm();
      } else {
        toastify(response.message || response.error || "Failed to update template", "error");
      }
    } catch (error) {
      console.error("Error updating template:", error);
      toastify("Failed to update template", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTemplateName("");
    setTemplateContent("");
    setTemplateDescription("");
    setSearchTerm("");
  };

  return (
    <Dialog
      header="Edit Lease Template"
      visible={visible}
      style={{
        width: "95vw",
        maxWidth: "1400px",
        height: '95vh',
      }}
      onHide={() => {
        onHide();
        resetForm();
      }}
      className="lease-template-dialog"
      maximizable
    >
      <div className="d-flex flex-column" style={{ height: 'calc(95vh - 8rem)' }}>
        {/* Main Content Area - Scrollable */}
        <div className="flex-grow-1 overflow-auto">
          <div className="row">
            {/* Left side - Template Editor */}
            <div className="col-lg-8">
              <div className="p-4 template-editor-container" style={{ height: '75vh', overflowY: 'auto' }}>
                {/* Template Name and Description */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label htmlFor="templateName" className="form-label fw-semibold">
                      <i className="ti ti-file-text me-2"></i>
                      Template Name <span className="text-danger">*</span>
                    </label>
                    <InputText
                      id="templateName"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      className="form-control"
                      placeholder="Enter template name"
                      maxLength={100}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="templateDescription" className="form-label fw-semibold">
                      <i className="ti ti-info-circle me-2"></i>
                      Description <span className="text-danger">*</span>
                    </label>
                    <InputText
                      id="templateDescription"
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e.target.value)}
                      className="form-control"
                      placeholder="Brief description of this template"
                      maxLength={200}
                    />
                  </div>
                </div>

                {/* Rich Text Editor */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="ti ti-edit me-2"></i>
                    Template Content <span className="text-danger">*</span>
                  </label>
                  <div className="lease-template-editor">
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={templateContent}
                      onChange={setTemplateContent}
                      modules={modules}
                      formats={formats}
                      style={{ minHeight: '400px' }}
                      placeholder="Start writing your lease template content here... Use the dynamic fields panel on the right to insert tenant, landlord, and lease data that will be automatically populated."
                    />
                  </div>
                </div>

                {/* Quick Insert Buttons */}
                <div className="quick-insert-buttons">
                  <h6 className="mb-3">
                    <i className="ti ti-zap me-2 text-primary"></i>
                    Quick Insert Elements
                  </h6>
                  <div className="alert alert-light mb-3">
                    <small className="text-muted">
                      Common lease template elements that you can quickly insert with one click.
                    </small>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => insertQuickFormat('signature')}
                      title="Insert signature line for parties to sign"
                    >
                      <i className="ti ti-writing me-1"></i>
                      Signature Line
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => insertQuickFormat('table')}
                      title="Insert a formatted table for structured data"
                    >
                      <i className="ti ti-table me-1"></i>
                      Table
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => insertQuickFormat('date')}
                      title="Insert today's date"
                    >
                      <i className="ti ti-calendar me-1"></i>
                      Current Date
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => insertQuickFormat('separator')}
                      title="Insert a horizontal line separator"
                    >
                      <i className="ti ti-minus me-1"></i>
                      Separator
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Placeholders Panel */}
            <div className="col-lg-4" style={{ borderLeft: "1px solid #e9ecef", background: "#fafafa" }}>
              <div className="p-4 dynamic-fields-container" style={{ height: '80vh', overflowY: 'auto' }}>
                <div className="d-flex align-items-center mb-4">
                  <i className="ti ti-code text-primary me-2" style={{ fontSize: '20px' }}></i>
                  <h5 className="mb-0 fw-bold text-primary">Dynamic Fields</h5>
                </div>

                {/* Search Placeholders */}
                <div className="mb-4">
                  <div className="position-relative">
                    <InputText
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search dynamic fields..."
                      className="search-input w-100 ps-5"
                      style={{ paddingLeft: '2.5rem' }}
                    />
                    <i className="ti ti-search position-absolute" style={{
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6c757d',
                      fontSize: '16px'
                    }}></i>
                    {searchTerm && (
                      <button
                        className="btn btn-sm position-absolute"
                        style={{
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          border: 'none',
                          background: 'transparent',
                          color: '#6c757d'
                        }}
                        onClick={() => setSearchTerm('')}
                        title="Clear search"
                      >
                        <i className="ti ti-x" style={{ fontSize: '14px' }}></i>
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <small className="text-muted mt-2 d-block">
                      <i className="ti ti-search me-1"></i>
                      Searching for: "{searchTerm}"
                    </small>
                  )}
                </div>

                {/* Usage Instructions */}
                <div className="alert alert-info mb-4">
                  <strong>
                    <i className="ti ti-info-circle me-2"></i>
                    How to use Dynamic Fields:
                  </strong>
                  <p className="mb-2">Click any field below to insert it into your template. These placeholders will be automatically replaced with actual lease data when the template is used to generate documents.</p>

                  <div className="example-block bg-white p-3 rounded mt-3">
                    <strong>Examples:</strong><br />
                    <code className="text-primary">{`{tenant.firstName}`}</code> → "John"<br />
                    <code className="text-primary">{`{lease.financialTerms.monthlyRent}`}</code> → "1500.00"<br />
                    <code className="text-primary">{`{currency.currencyShortCode} {lease.financialTerms.monthlyRent}`}</code> → "USD 1500.00"
                  </div>

                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="ti ti-lightbulb me-1"></i>
                      <strong>Tip:</strong> Use the search box above to quickly find specific fields you need.
                    </small>
                  </div>
                </div>

                {/* Placeholder Fields */}
                <div className="placeholders-container">
                  {Object.entries(placeholders).map(([category, fields]) =>
                    renderPlaceholderSection(category, fields)
                  )}

                  {/* No results message */}
                  {searchTerm && Object.entries(placeholders).every(([, fields]) =>
                    filterPlaceholders(fields).length === 0
                  ) && (
                      <div className="text-center py-5">
                        <i className="ti ti-search-off text-muted" style={{ fontSize: '3rem' }}></i>
                        <h6 className="text-muted mt-3">No fields found</h6>
                        <p className="text-muted mb-3">
                          No dynamic fields match your search for "<strong>{searchTerm}</strong>"
                        </p>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setSearchTerm('')}
                        >
                          <i className="ti ti-x me-1"></i>
                          Clear Search
                        </button>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div
          className="flex-shrink-0 d-flex justify-content-between align-items-center p-4"
          style={{
            borderTop: "1px solid #e9ecef",
            background: "#f8f9fa",
          }}
        >

          <Button
            label="Cancel"
            className="p-button-secondary p-button-outlined"
            onClick={() => {
              onHide();
              resetForm();
            }}
            disabled={loading}
          />
          <Button
            label="Update Template"
            className="p-button-primary"
            onClick={handleSaveTemplate}
            disabled={!templateName || !templateContent || !templateDescription}
            loading={loading}
          />
        </div>

      </div>

      {/* Image Resize Dialog */}
      <Dialog
        header="Resize Image"
        visible={imageResizeVisible}
        style={{ width: '450px' }}
        onHide={() => {
          setImageResizeVisible(false);
          setSelectedImage(null);
        }}
        modal
      >
        <div className="image-resize-dialog">
          {/* Quick Size Buttons */}
          <div className="mb-4">
            <h6 className="mb-3">Quick Sizes</h6>
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => applyImageSize('small')}
              >
                Small (25%)
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => applyImageSize('medium')}
              >
                Medium (50%)
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => applyImageSize('large')}
              >
                Large (75%)
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => applyImageSize('full')}
              >
                Full Width (100%)
              </button>
            </div>
          </div>

          {/* Custom Size Controls */}
          <div className="mb-4">
            <h6 className="mb-3">Custom Size</h6>
            <div className="row">
              <div className="col-6">
                <label className="form-label">Width (%)</label>
                <input
                  type="range"
                  className="form-range"
                  min="10"
                  max="100"
                  value={currentImageSize.width}
                  onChange={(e) => setCurrentImageSize(prev => ({
                    ...prev,
                    width: parseInt(e.target.value)
                  }))}
                />
                <div className="text-center">
                  <small className="text-muted">{currentImageSize.width}%</small>
                </div>
              </div>
              <div className="col-6">
                <label className="form-label">Height</label>
                <select
                  className="form-select form-select-sm"
                  value={currentImageSize.height}
                  onChange={(e) => setCurrentImageSize(prev => ({
                    ...prev,
                    height: e.target.value === 'auto' ? 'auto' : parseInt(e.target.value)
                  }))}
                >
                  <option value="auto">Auto</option>
                  <option value="100">100px</option>
                  <option value="150">150px</option>
                  <option value="200">200px</option>
                  <option value="250">250px</option>
                  <option value="300">300px</option>
                  <option value="400">400px</option>
                  <option value="500">500px</option>
                </select>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mb-4">
            <h6 className="mb-2">Preview Size</h6>
            <div
              className="border p-2 text-center bg-light"
              style={{ minHeight: '60px' }}
            >
              <div
                style={{
                  width: `${currentImageSize.width}%`,
                  height: currentImageSize.height === 'auto' ? '40px' : `${Math.min(currentImageSize.height, 50)}px`,
                  backgroundColor: '#007bff',
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  borderRadius: '4px'
                }}
              >
                {currentImageSize.width}% × {currentImageSize.height === 'auto' ? 'Auto' : `${currentImageSize.height}px`}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-between">
            <button
              className="btn btn-danger btn-sm"
              onClick={removeImage}
            >
              <i className="ti ti-trash me-1"></i>
              Remove Image
            </button>
            <div className="d-flex gap-2">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setImageResizeVisible(false);
                  setSelectedImage(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleImageResize}
              >
                Apply Size
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      <style>{`
        .lease-template-dialog .p-dialog-content {
          padding: 0;
          overflow: visible;
          height: auto !important;
          max-height: calc(95vh - 6rem);
          position: relative;
          display: flex;
          flex-direction: column;
        }

        .ql-container {
          flex: 1;
          overflow-y: auto !important;
          min-height: 300px;
          max-height: 450px;
          border-radius: 0 0 8px 8px;
        }

        /* Template editor container */
        .template-editor-container {
          position: relative;
        }

        /* Dynamic fields container */
        .dynamic-fields-container {
          position: relative;
        }

        /* Custom scrollbars for better UX */
        .ql-container::-webkit-scrollbar,
        .template-editor-container::-webkit-scrollbar,
        .dynamic-fields-container::-webkit-scrollbar {
          width: 6px;
        }

        .ql-container::-webkit-scrollbar-track,
        .template-editor-container::-webkit-scrollbar-track,
        .dynamic-fields-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .ql-container::-webkit-scrollbar-thumb,
        .template-editor-container::-webkit-scrollbar-thumb,
        .dynamic-fields-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .ql-container::-webkit-scrollbar-thumb:hover,
        .template-editor-container::-webkit-scrollbar-thumb:hover,
        .dynamic-fields-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        .ql-toolbar {
          border-radius: 8px 8px 0 0;
          border-bottom: 1px solid #ccc;
          background: #f8f9fa;
        }

        .ql-editor {
          min-height: 100%;
          font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
          font-size: 14px;
          line-height: 1.6;
        }

        /* Enhanced image styling within editor */
        .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin: 10px 0;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 2px solid transparent;
        }

        .ql-editor img:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          border-color: #007bff;
        }

        .ql-editor img:active {
          border-color: #0056b3;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
        }

        /* Image resize dialog styling */
        .image-resize-dialog .form-range {
          background: linear-gradient(to right, #007bff 0%, #007bff 100%);
        }

        .image-resize-dialog .form-range::-webkit-slider-thumb {
          background: #007bff;
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .image-resize-dialog .form-range::-moz-range-thumb {
          background: #007bff;
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        /* Quick size buttons */
        .image-resize-dialog .btn-outline-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,123,255,0.3);
        }

        /* Size preview box */
        .image-resize-dialog .bg-light {
          background: repeating-linear-gradient(
            45deg,
            #f8f9fa,
            #f8f9fa 10px,
            #ffffff 10px,
            #ffffff 20px
          ) !important;
        }

        /* Fix placeholder visibility */
        .placeholders-container {
          position: relative;
          z-index: 10;
          background: transparent;
        }

        .field-category {
          position: relative;
          z-index: 11;
          background: transparent;
        }

        .field-category .btn {
          position: relative;
          z-index: 12;
          background: white;
          border: 1px solid #dee2e6;
        }

        /* Table styling */
        .ql-editor table {
          border-collapse: collapse;
          margin: 15px 0;
          width: 100%;
        }

        .ql-editor table td,
        .ql-editor table th {
          border: 1px solid #ddd;
          padding: 8px 12px;
          text-align: left;
        }

        .ql-editor table th {
          background-color: #f5f5f5;
          font-weight: 600;
        }

        /* Enhanced blockquote styling */
        .ql-editor blockquote {
          border-left: 4px solid #2196f3;
          padding-left: 16px;
          margin: 15px 0;
          background-color: #f8f9fa;
          padding: 12px 16px;
          border-radius: 0 4px 4px 0;
        }

        /* Code block styling */
        .ql-editor pre {
          background-color: #f4f4f4;
          border: 1px solid #e1e1e1;
          border-radius: 4px;
          padding: 12px;
          margin: 10px 0;
          font-family: 'Courier New', monospace;
        }

        /* Quick format buttons hover effects */
        .quick-insert-buttons .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        /* Placeholder buttons enhanced styling */
        .field-category .btn:hover {
          transform: translateX(3px);
          border-left-width: 4px;
          box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
        }

        /* Enhanced toolbar styling */
        .ql-toolbar .ql-picker-label:hover,
        .ql-toolbar .ql-picker-item:hover {
          color: #2196f3;
        }

        .ql-toolbar button:hover {
          color: #2196f3;
        }

        .ql-toolbar button.ql-active {
          color: #2196f3;
        }

        /* Dialog header enhancement */
        .lease-template-dialog .p-dialog-header {
          background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
          color: white;
          border-radius: 8px 8px 0 0;
        }

        .lease-template-dialog .p-dialog-header .p-dialog-title {
          color: white;
          font-weight: 600;
        }

        .lease-template-dialog .p-dialog-header-icon {
          color: white;
        }
      `}</style>
    </Dialog>
  );
};

// Button wrapper component that matches the original interface
const EditButton = ({ template, onEdit }) => {
  const [showDialog, setShowDialog] = useState(false);

  return (
    <>
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info"
        onClick={() => setShowDialog(true)}
        tooltip="Edit Template"
      />

      <EditTemplateDialog
        template={template}
        visible={showDialog}
        onHide={() => setShowDialog(false)}
        onEdit={onEdit}
      />
    </>
  );
};

export default EditButton;